// Home.jsx
import React, { Component } from "react";
import { Button } from "react-bootstrap";
import CartContext from "./CartContext";
import SearchViewPOS from "./SearchViewPOS";
import FavList from "./FavList";
import CartViewPOS, { OrderType } from "./CartViewPOS";
import BottomSummaryPOS from "./BottomSummaryPOS";
import TableOrder from "./TableOrder";
import ElasticCartAd from "./ElasticCartAd";
import OrderLabelManager from "./OrderLabelManager";

const RESIZE_DEBOUNCE_MS = 200;
const POS_IDS = ["POS1", "POS2", "POS3", "POS4", "POS5"];

export default class Home extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    this.state = {
      isDesktop:
        typeof window !== "undefined" ? window.innerWidth >= 900 : true,
      leftRailCollapsed: false,
      showProductListByCategory: false,
      updateCartView: true,
      havePermissions: false,
      coupon: { couponCode: "", couponValue: 0 },
      quickPayEventSent: false,
      quickPayEventKey: "",
      reloadCheckoutPos: false,
      orderTypeOrderDelivered: true,
      orderTypeTakeAway: false,
      orderTypeHomeDelivery: false,
      showElasticCart: true,
      myClientIpAddress: "",
      selectedTableId: "",
    };
    this._isMounted = false;
    this.ipAbort = null;
    this.resizeTimer = null;
  }

  // ---------- helpers (unchanged where not shown) ----------
  StoreCouponDetails = (couponCode, couponValue) =>
    this.setState({ coupon: { couponCode, couponValue } });

  SetQuickPayEventFalse = () =>
    this.setState({ quickPayEventSent: false, quickPayEventKey: "" });

  UpdateCartView = () =>
    this.setState({ updateCartView: false }, () =>
      this.setState({ updateCartView: true }),
    );

  POSButtonClick = (e) => {
    const id = e.currentTarget?.id;
    this.setState({ selectedTableId: "" }, () => {
      this.SetOrderTypeState(id);
      this.triggerCheckoutReload();
    });
  };

  triggerCheckoutReload = () =>
    this.setState({ reloadCheckoutPos: true }, () =>
      this.setState({ reloadCheckoutPos: false }),
    );

  SetOrderTypeState = (posName) => {
    const selectedPos = this.context.posCarts?.find((c) => c?.name === posName);
    if (!selectedPos) {
      this.context.SetSelectedPOS(posName);
      return;
    }
    const { orderType } = selectedPos;
    const patch =
      orderType === "InStore"
        ? {
            orderTypeOrderDelivered: true,
            orderTypeTakeAway: false,
            orderTypeHomeDelivery: false,
          }
        : orderType === "TakeAway"
          ? {
              orderTypeOrderDelivered: false,
              orderTypeTakeAway: true,
              orderTypeHomeDelivery: false,
            }
          : orderType === "HomeDelivery"
            ? {
                orderTypeOrderDelivered: false,
                orderTypeTakeAway: false,
                orderTypeHomeDelivery: true,
              }
            : {
                orderTypeOrderDelivered: true,
                orderTypeTakeAway: false,
                orderTypeHomeDelivery: false,
              };
    this.setState(patch, () => this.context.SetSelectedPOS(posName));
  };

  handleChangeOrderType = (ordertype) => {
    const patch =
      ordertype === "InStore"
        ? {
            orderTypeOrderDelivered: true,
            orderTypeTakeAway: false,
            orderTypeHomeDelivery: false,
          }
        : ordertype === "TakeAway"
          ? {
              orderTypeOrderDelivered: false,
              orderTypeTakeAway: true,
              orderTypeHomeDelivery: false,
            }
          : {
              orderTypeOrderDelivered: false,
              orderTypeTakeAway: false,
              orderTypeHomeDelivery: true,
            };
    this.setState(patch, () => {
      this.context.UpdatePOSCartWithNewPrice(this.context.selectedPOS);
      this.triggerCheckoutReload();
    });
  };

  _handleNVEvent = (event) => {
    const key = event.key;
    const cart = this.context.getPOSCartByName(this.context.selectedPOS) ?? [];
    if (!cart.length) return;
    if ((key === "F8" || key === "F9") && !this.state.quickPayEventSent) {
      event.preventDefault();
      event.stopPropagation();
      this.setState({ quickPayEventSent: true, quickPayEventKey: key }, () =>
        this.triggerCheckoutReload(),
      );
    }
  };

  handleResize = () => {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this._isMounted) return;
      this.setState({ isDesktop: window.innerWidth >= 900 });
    }, RESIZE_DEBOUNCE_MS);
  };

  getIPAddress = () => {
    if (this.context.isOfflineMode) return;
    try {
      this.ipAbort = new AbortController();
      fetch("https://api.ipify.org/?format=json", {
        signal: this.ipAbort.signal,
      })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then(
          (d) =>
            this._isMounted &&
            d?.ip &&
            this.setState({ myClientIpAddress: d.ip }),
        )
        .catch(() => {});
    } catch {}
  };

  componentDidMount() {
    this._isMounted = true;
    this.context.forceUpdateCartView();
    this.getIPAddress();
    document.addEventListener("keydown", this._handleNVEvent, false);
    window.addEventListener("resize", this.handleResize, false);

    const kickOff = () => {
      if (!this._isMounted) return;
      this.setState({ showElasticCart: false }, () => {
        this.SetOrderTypeState(this.context.selectedPOS);
      });
    };
    if (!this.context.prods || this.context.prods.length === 0) {
      setTimeout(kickOff, 300);
    } else {
      kickOff();
    }
  }

  componentDidUpdate() {
    this.context.SetQtyInCartModified(false);
    if (this.context.forceUpdatePosCart === true) {
      this.context.SetForceUpdatePosCart(false);
      this.triggerCheckoutReload();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.removeEventListener("keydown", this._handleNVEvent, false);
    window.removeEventListener("resize", this.handleResize, false);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    if (this.ipAbort) this.ipAbort.abort();
  }

  // ---------- UI helpers ----------
  MiniCartBadge = () => {
    const qty = this.context.getTotalQtyInCart(
      this.context.getPOSCartByName(this.context.selectedPOS),
    );
    const total = this.context.getTotalAmountWithTax(
      this.context.getPOSCartByName(this.context.selectedPOS),
      this.state.coupon.couponValue,
    );
    return (
      <div
        style={{
          position: "sticky",
          top: 8,
          zIndex: 10,
          display: "flex",
          gap: 8,
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "6px 10px",
          borderRadius: 10,
          background: "rgba(255,255,255,0.9)",
          boxShadow: "0 2px 10px rgba(0,0,0,.06)",
          margin: "8px",
        }}
      >
        <span style={{ fontSize: 12, opacity: 0.8 }}>Items</span>
        <strong style={{ fontSize: 13 }}>{qty}</strong>
        <span
          style={{ width: 1, height: 16, background: "#ddd", margin: "0 6px" }}
        />
        <span style={{ fontSize: 12, opacity: 0.8 }}>Total</span>
        <strong style={{ fontSize: 14 }}>
          {Math.round(total, 0).toLocaleString(
            this.context.storeSettings.defaultLocale,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              style: "currency",
              currency: this.context.storeSettings.defaultCurrency,
            },
          )}
        </strong>
      </div>
    );
  };

  POSRail = () => {
    const { leftRailCollapsed } = this.state;
    return (
      <div
        style={{
          width: leftRailCollapsed ? 40 : 64,
          padding: "6px 4px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          borderRight: "1px solid rgba(0,0,0,.05)",
        }}
      >
        <Button
          size="sm"
          variant={leftRailCollapsed ? "secondary" : "light"}
          style={{ width: "100%", fontWeight: 700 }}
          onClick={() =>
            this.setState({ leftRailCollapsed: !leftRailCollapsed })
          }
          title={leftRailCollapsed ? "Expand" : "Collapse"}
        >
          {leftRailCollapsed ? "»" : "«"}
        </Button>

        {POS_IDS.map((id, idx) => {
          const active = this.context.selectedPOS === id;
          return (
            <Button
              key={id}
              id={id}
              style={{
                width: "100%",
                height: 44,
                fontSize: 12,
                fontWeight: 700,
              }}
              variant={active ? "warning" : "info"}
              onClick={this.POSButtonClick}
              aria-pressed={active}
              title={`Counter ${idx + 1}`}
            >
              {`C${idx + 1}`}
            </Button>
          );
        })}
        <OrderLabelManager ref={(r) => (this.orderLabelMgr = r)} />

        <Button
          size="sm"
          variant="light"
          style={{ width: "100%", fontWeight: 700 }}
          onClick={() => this.orderLabelMgr?.open()}
        >
          Order & Labels
        </Button>
      </div>
    );
  };

  // ---------- layouts ----------
  renderDesktop = () => {
    const bodyBg = this.context.posBackgroundColor;

    return (
      <div
        className="m-0 p-0"
        style={{ backgroundColor: bodyBg, minHeight: "100vh" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            gap: 8,
            padding: 8,
          }}
        >
          {/* left rail */}
          {this.POSRail()}

          {/* middle: search/fav */}
          <div style={{ flex: "1 1 55%", minWidth: 0 }}>
            {this.context.hideFav ? (
              <SearchViewPOS />
            ) : (
              <div className="m-0 p-2 bg-light">
                <FavList />
              </div>
            )}
          </div>

          {/* right: cart */}
          <div
            className="py-1"
            style={{
              flex: "1 1 45%",
              minWidth: 500,
              maxWidth: 750,
              background: bodyBg,
              overflow: "hidden",
              borderLeft: "1px solid rgba(0,0,0,.05)",
              borderRadius: 8,
            }}
          >
            <div
              className="m-0 p-2 CornersRoundedSmall "
              style={{
                backgroundColor: "white",
                height: `calc(100vh - 100px)`,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Always render CartViewPOS unless we are in the very short reload window */}
              {!this.state.reloadCheckoutPos && (
                <>
                  <div style={{ overflow: "auto" }}>
                    <CartViewPOS
                      updateCartView={this.UpdateCartView}
                      myClientIpAddress={this.state.myClientIpAddress}
                      handleChangeOrderType={this.handleChangeOrderType}
                    />
                  </div>
                  <div style={{ flex: "0 0 auto" }}>
                    {this.context.getTotalQtyInCart(
                      this.context.getPOSCartByName(this.context.selectedPOS),
                    ) > 0 && (
                      <BottomSummaryPOS
                        StoreCouponDetails={this.StoreCouponDetails}
                        coupon={this.state.coupon}
                        quickPayEventKey={this.state.quickPayEventKey}
                        quickPayEventSent={this.state.quickPayEventSent}
                        SetQuickPayEventFalse={this.SetQuickPayEventFalse}
                        orderTypeOrderDelivered={
                          this.state.orderTypeOrderDelivered
                        }
                        orderTypeTakeAway={this.state.orderTypeTakeAway}
                        orderTypeHomeDelivery={this.state.orderTypeHomeDelivery}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* table order full takeover */}
        {this.state.selectedTableId !== "" && (
          <div style={{ padding: 8 }}>
            <TableOrder />
          </div>
        )}
      </div>
    );
  };

  renderMobile = () => {
    return (
      <div className="m-0 py-0 w-100">
        <div
          className="text-center w-100 bg-dark pt-2"
          style={{ fontFamily: "Tahoma" }}
        >
          {POS_IDS.map((id, idx) => (
            <Button
              key={id}
              id={id}
              className="mx-1"
              style={{ fontSize: 16, fontWeight: "bold" }}
              variant={this.context.selectedPOS === id ? "warning" : "primary"}
              onClick={this.POSButtonClick}
            >
              {`C${idx + 1}`}
            </Button>
          ))}
        </div>

        <div className="bg-light">
          <OrderType handleChangeOrderType={this.handleChangeOrderType} />
        </div>

        <SearchViewPOS />

        <div className="m-0 p-0">
          <CartViewPOS
            updateCartView={this.UpdateCartView}
            myClientIpAddress={this.state.myClientIpAddress}
            handleChangeOrderType={this.handleChangeOrderType}
          />
          {this.context.getTotalQtyInCart(
            this.context.getPOSCartByName(this.context.selectedPOS),
          ) > 0 && (
            <BottomSummaryPOS
              StoreCouponDetails={this.StoreCouponDetails}
              coupon={this.state.coupon}
              quickPayEventKey={this.state.quickPayEventKey}
              quickPayEventSent={this.state.quickPayEventSent}
              SetQuickPayEventFalse={this.SetQuickPayEventFalse}
              orderTypeOrderDelivered={this.state.orderTypeOrderDelivered}
              orderTypeTakeAway={this.state.orderTypeTakeAway}
              orderTypeHomeDelivery={this.state.orderTypeHomeDelivery}
            />
          )}
        </div>
      </div>
    );
  };

  // ---------- main render ----------
  render() {
    if (this.state.showElasticCart) return <ElasticCartAd />;
    if (!this.context.prodsAll || this.context.prodsAll.length === 0)
      return <ElasticCartAd />;

    // license gate unchanged
    if (
      this.context.storeSettings?.posLicenseKey != null &&
      this.context.storeSettings.posLicenseKey !==
        this.context.GetLicenseKey() &&
      this.state.isDesktop
    ) {
      return <></>;
    }

    return this.state.isDesktop ? this.renderDesktop() : this.renderMobile();
  }
}
